/**
 * 发单剧本：上海日历日、时间窗与内容规范化（快照 / 匹配共用）
 */

export function getShanghaiDateKey(date: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

export function getShanghaiMinutesOfDay(date: Date): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Shanghai",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);

  const hour = Number(parts.find((part) => part.type === "hour")?.value ?? "0");
  const minute = Number(parts.find((part) => part.type === "minute")?.value ?? "0");
  return hour * 60 + minute;
}

/** 上海日历日 [start, end]（含当日最后一毫秒） */
export function shanghaiDayBounds(scriptDate: string): { start: Date; end: Date } {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(scriptDate)) {
    throw new Error(`无效的剧本日期: ${scriptDate}`);
  }
  return {
    start: new Date(`${scriptDate}T00:00:00.000+08:00`),
    end: new Date(`${scriptDate}T23:59:59.999+08:00`),
  };
}

const URL_PATTERN = /https?:\/\/[^\s]+/gi;
const LINK_PLACEHOLDER_PATTERN = /<link>/gi;

export function normalizeDispatchScriptText(value: string): string {
  return value
    .replace(URL_PATTERN, "")
    .replace(LINK_PLACEHOLDER_PATTERN, "")
    .replace(/[\uFE0E\uFE0F]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

export const DISPATCH_SCRIPT_MATCH_STATUSES = [
  "pending",
  "exact",
  "short",
  "excess",
  "skipped",
] as const;

export type DispatchScriptMatchStatus = (typeof DISPATCH_SCRIPT_MATCH_STATUSES)[number];

export const DISPATCH_SCRIPT_MATCH_STATUS_LABELS: Record<DispatchScriptMatchStatus, string> = {
  pending: "待匹配",
  exact: "正好",
  short: "少了",
  excess: "多了",
  skipped: "未监测",
};

/** 当日分钟数 → HH:mm */
export function formatShanghaiMinutesOfDay(totalMinutes: number): string {
  const safe = Math.max(0, Math.min(24 * 60 - 1, Math.floor(totalMinutes)));
  const hour = Math.floor(safe / 60);
  const minute = safe % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

export function computeDispatchScriptMatchStatus(input: {
  configuredRoomCount: number;
  hitRoomCount: number;
  extraRoomCount: number;
}): DispatchScriptMatchStatus {
  if (input.hitRoomCount === 0 && input.extraRoomCount === 0) {
    return input.configuredRoomCount === 0 ? "exact" : "pending";
  }
  if (input.extraRoomCount > 0 || input.hitRoomCount > input.configuredRoomCount) {
    return "excess";
  }
  if (input.hitRoomCount < input.configuredRoomCount) {
    return "short";
  }
  return "exact";
}

export interface DispatchScriptTimeDeviation {
  /** 相对预设时间，各命中群「首条匹配消息」偏离分钟数的最小值（越早越小，可为负） */
  minMinutes: number;
  /** 平均值 */
  avgMinutes: number;
  /** 最大值（最晚） */
  maxMinutes: number;
  /** 参与统计的命中群数 */
  sampleCount: number;
}

/**
 * 相对剧本预设时间的偏离。
 * presetMinutes 一般用 windowStartMinutes（剧本标注点）。
 * roomFirstHitMinutes：每个命中群内最早一条匹配消息的当日分钟数。
 */
export function computeDispatchScriptTimeDeviation(
  presetMinutes: number,
  roomFirstHitMinutes: number[]
): DispatchScriptTimeDeviation | null {
  if (!roomFirstHitMinutes.length || !Number.isFinite(presetMinutes)) {
    return null;
  }
  const deviations = roomFirstHitMinutes.map((minutes) => minutes - presetMinutes);
  const minMinutes = Math.min(...deviations);
  const maxMinutes = Math.max(...deviations);
  const avgMinutes =
    Math.round((deviations.reduce((sum, value) => sum + value, 0) / deviations.length) * 10) / 10;
  return {
    minMinutes,
    avgMinutes,
    maxMinutes,
    sampleCount: deviations.length,
  };
}

/** 展示用：晚了3分钟 / 提前2分钟 / 准时 */
export function formatDispatchScriptTimeDeviationMinutes(minutes: number | null | undefined): string {
  if (minutes == null || !Number.isFinite(minutes)) return "—";
  const rounded = Math.round(minutes);
  if (rounded === 0) return "准时";
  return rounded > 0 ? `晚了${rounded}分钟` : `提前${Math.abs(rounded)}分钟`;
}
